import React, { useEffect, useState } from 'react';
import { find } from 'lodash';

import { OrderBlock } from '@/components/modals/SubmitOrder/components/Common/OrderBlock';
import { getEquipmentName, getOrderItemPrice } from '@/components/modals/SubmitOrder/constants';
import { useOrderContext } from '@/components/modals/SubmitOrder/helpers';
import { OrderItemWrapper } from '@/components/modals/SubmitOrder/types';
import { PriceBookEntryVm } from '@/types';

import { ProductQuantity } from '../../../ProductQuantity';

import { ShingleColorTile } from './components/ShingleColorTile';
import { hasAvailableShingles, shouldShowShingleDetails } from './constants';
import { ComponentWrapperProps, Props } from './types';

import styles from './ShingleSection.module.scss';

const componentWrapper = (props: ComponentWrapperProps): JSX.Element => {
  const { equipmentName, equipmentPrice, isPriceHidden, children } = props;

  return (
    <OrderBlock
      color="#FAFAFA"
      divider
      hideValue={isPriceHidden}
      smallHeader
      title={equipmentName}
      value={equipmentPrice}
    >
      {children}
    </OrderBlock>
  );
};

export const ShingleSection = (props: Props) => {
  const { proposalItems, availableAsphaltShingles, selectedAsphaltShingle, selectAsphaltShingle } =
    props;
  const state = useOrderContext();
  const [squares, setSquares] = useState(0);
  const [canIncrease, setCanIncrease] = useState<boolean | null>(null);
  const [canDecrease, setCanDecrease] = useState<boolean | null>(null);

  useEffect(() => {
    // set initial value of squares by calculating it from asphalt shingle quantity
    // only when user have not changed squares quantity from the UI yet
    const shouldSetInitialSquares =
      selectedAsphaltShingle?.orderItem.numberOfSquares && squares === 0;

    if (shouldSetInitialSquares) {
      setSquares(selectedAsphaltShingle?.orderItem.numberOfSquares as number);
    }

    if (selectedAsphaltShingle?.priceBookEntry) {
      setCanIncrease(selectedAsphaltShingle?.priceBookEntry.rooferCanIncreaseQuantity ?? false);
      setCanDecrease(selectedAsphaltShingle?.priceBookEntry.rooferCanDecreaseQuantity ?? false);
    }
  }, [selectedAsphaltShingle?.priceBookEntry]);

  const calculateQuantity = (numberOfSquares: number, color?: PriceBookEntryVm) => {
    const colorForCalculation = color ?? selectedAsphaltShingle?.priceBookEntry;
    const quantityRatio = colorForCalculation?.product?.squareToQuantityRatio ?? 1;

    return Math.ceil(numberOfSquares / quantityRatio);
  };

  const calculateBundles = (quantity: number) => {
    const bundleRatio = selectedAsphaltShingle?.priceBookEntry?.product?.bundleToQuantityRatio ?? 1;

    return Math.ceil(quantity / bundleRatio);
  };

  const onQuantitySelected = (numberOfSquares: number) => {
    if (!numberOfSquares) {
      setSquares(0);
      selectAsphaltShingle(null as unknown as OrderItemWrapper, 0);

      return;
    }

    if (selectedAsphaltShingle === null) {
      setSquares(numberOfSquares);
      selectAsphaltShingle(null as unknown as OrderItemWrapper, numberOfSquares);

      return;
    }

    const quantity = calculateQuantity(numberOfSquares);
    const numberOfBundles = calculateBundles(quantity);

    selectAsphaltShingle({
      orderItem: {
        ...selectedAsphaltShingle.orderItem,
        numberOfSquares,
        quantity,
        numberOfBundles,
      },
      priceBookEntry: selectedAsphaltShingle.priceBookEntry,
      proposalItem: selectedAsphaltShingle.proposalItem,
    });

    setSquares(numberOfSquares);
  };

  const onColorSelected = (color: PriceBookEntryVm) => {
    let numberOfSquares = squares;
    const { rooferCanIncreaseQuantity, rooferCanDecreaseQuantity } = color;

    setCanIncrease(rooferCanIncreaseQuantity ?? false);
    setCanDecrease(rooferCanDecreaseQuantity ?? false);

    if (!rooferCanDecreaseQuantity || !rooferCanIncreaseQuantity) {
      setSquares(1);
      numberOfSquares = 1;
    }

    const proposalItem = find(proposalItems, pi => pi.product.id === color.productId) ?? null;
    const quantity = calculateQuantity(numberOfSquares, color);
    const numberOfBundles = calculateBundles(quantity);

    selectAsphaltShingle({
      orderItem: {
        quantity,
        numberOfSquares,
        numberOfBundles,
        productId: color.productId,
        description: null,
        linearFeetValue: null,
        quantityOfOverage: 0,
        quantityByDesign: 0,
        totalOutputKw: null,
        totalOutputW: null,
      },
      priceBookEntry: color,
      proposalItem: proposalItem,
    });
  };

  const equipmentName = getEquipmentName(selectedAsphaltShingle);
  const equipmentPrice = selectedAsphaltShingle
    ? getOrderItemPrice(state, selectedAsphaltShingle)
    : 0;

  const canAdjustQuantity = (isIncreaseButton: boolean): boolean => {
    if (selectedAsphaltShingle) {
      return isIncreaseButton ? canIncrease || squares === 0 : canDecrease || squares === 1;
    }

    return true;
  };

  if (hasAvailableShingles(availableAsphaltShingles)) {
    return null;
  }

  const component = (
    <>
      <div
        className={styles.shingleSectionPanel}
        style={{ display: shouldShowShingleDetails(squares, selectedAsphaltShingle) }}
      >
        <div className={styles.panelTitle}>
          <span>Shingle Color</span>
          <span>Total Shipped</span>
        </div>
        <div className={styles.panelValues}>
          <span data-testid="shingle-color-title">
            {selectedAsphaltShingle?.priceBookEntry?.product?.asphaltShingleColor}
          </span>
          <span>{selectedAsphaltShingle?.orderItem.numberOfBundles} BD</span>
        </div>
      </div>
      <div className={styles.shingleSection}>
        <ProductQuantity
          quantity={squares}
          selectQuantity={onQuantitySelected}
          subtitle="Enter the amount in SQ (squares) and the number of bundles will be automatically calculated."
          units="SQ"
        />
        <br />
        <span>The purchase of asphalt shingles is optional.</span>
        {squares > 0 && (
          <div className={styles.selectShingleColorWrapper}>
            <hr />
            <span className={styles.title}>Select Shingle Color:</span>
            <div className={styles.selectShingleColorSection}>
              {availableAsphaltShingles?.map(asphaltShingle => (
                <ShingleColorTile
                  isActive={
                    asphaltShingle.productId === selectedAsphaltShingle?.orderItem.productId
                  }
                  key={asphaltShingle.productId}
                  selectColor={onColorSelected}
                  shingleColor={asphaltShingle}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );

  return componentWrapper({
    equipmentName,
    equipmentPrice,
    isPriceHidden: state.isPriceHidden,
    children: component,
  });
};
